import React, { useEffect, useState } from "react";
import {
  Button,
  DatePicker,
  Form,
  Input,
  Modal,
  Select,
  Row,
  Col,
} from "antd";
import { UsimInventoryItem } from "../../../../api/usim/inventory/inventoryItem";
import dayjs from "dayjs";
import { useGetProductPlansQuery } from "../../../../api/usim/uploads/products/productPlansApi";
import { useGetProductTypesQuery } from "../../../../api/usim/uploads/products/productTypeApi";
import { UsimSkuItem } from "../../../../api/usim/uploads/products/usimSkuItem";
import { UsimTypeItem } from "../../../../api/usim/uploads/products/usimTypeItem";

const { TextArea } = Input;

interface InventoryAddModalProps {
  isVisible: boolean;
  onClose: () => void;
  onSubmit: (data: UsimInventoryItem) => Promise<void>;
  isUpdate: boolean;
  data?: UsimInventoryItem;
  inventoryAddOptions: { label: string; value: string; }[];
}

export default function InventoryAddModal({
  isVisible,
  onClose,
  onSubmit,
  isUpdate,
  data,
  inventoryAddOptions,
}: InventoryAddModalProps) {
  const [form] = Form.useForm();

  const { data: productPlans, error: productPlansError, isFetching: productPlansFetching } = useGetProductPlansQuery();
  const { data: productTypes, error: productTypesError, isFetching: productTypesFetching } = useGetProductTypesQuery();


    // Transform product types for dropdown options
    const productPlansOptions = React.useMemo(() => {
      if (!productPlans) return [];
      //do not show item if status is INACTIVE
      return productPlans.filter(item => item.status !== "INACTIVE").map(item => ({
        label: item.planName,
        value: item.planName,
      }));
    }, [productPlans]);
     
    // get product types for dropdown option
    const productTypesOptions = React.useMemo(() => {
      if (!productTypes) return [];
      //do not show item if status is INACTIVE
      return productTypes.filter(item => item.status !== "INACTIVE").map(item => ({
        label: item.name,
        value: item.name,
      }));
    }, [productTypes]);
  // Reset form when modal opens/closes or data changes
  useEffect(() => {
    if (isVisible) {
      if (isUpdate && data) {
        form.setFieldsValue({
          productType: data.productType,
          productPlan: data.planName,
          expiryDate: data.expiryDate,
          iccid: data.ICCID,
          mobileNumber: data.mobileNumber,
          activationDate: data.activationDate,
          activatedDate: data.activatedDate,
          ordernumber: Array.isArray(data.ordernumber) ? data.ordernumber.join(", ") : data.ordernumber,
          inwardTime: data.inwardTime,
        });
      } else {
        form.resetFields();
      }
    }
  }, [isVisible, isUpdate, data, form]);

  // Handle product plan selection to auto-fill product type
  const handleProductPlanChange = (planName: string) => {
    const selectedPlan = productPlans?.find(plan => plan.planName === planName);
    if (selectedPlan) {
      form.setFieldsValue({
        productType: selectedPlan.type
      });
    }
  };
  
  // handle type change
  const handleProductTypeChange = (type: string) => {
    const selectedType = productTypes?.find(item => item.name === type);
    // if (selectedType) {
    //   form.setFieldsValue({
    //     productPlan: selectedType.
    //   });
    // }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const inventoryItem: UsimInventoryItem = {
        Id: data?.Id || "",
        ICCID: values.iccid,
        mobileNumber: values.mobileNumber,
        productType: values.productType,
        planName: values.productPlan,
        expiryDate: values.expiryDate,
        activationDate: values.activationDate,
        activatedDate: values.activatedDate,
        ordernumber: values.ordernumber,
        inwardTime: values.inwardTime,
        UpdatedAt: data?.UpdatedAt || "",
        CreatedAt: data?.CreatedAt || "",
      };

      if(isUpdate) {
        inventoryItem.Id = data?.Id || "";
      }
      
      await onSubmit(inventoryItem);
      form.resetFields();
      onClose();
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={isUpdate ? "Edit Inventory" : "Add Inventory"}
      open={isVisible}
      onOk={handleSubmit}
      onCancel={handleCancel}
      okText={isUpdate ? "Update" : "Add"}
      cancelText="Cancel"
      width={800}
      style={{ maxHeight: '80vh' }}
      bodyStyle={{ maxHeight: '60vh', overflowY: 'auto' }}
    >
      <Form
        form={form}
        layout="vertical"
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="ICCID"
              name="iccid"
              rules={[{ required: true, message: "ICCID is required" }]}
            >
              <Input placeholder="Enter ICCID" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Mobile Number"
              name="mobileNumber"
            >
              <Input placeholder="Enter mobile number" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Product SKU"
              name="productPlan"
              // rules={[{ required: true, message: "Product SKU is required" }]}
            >
              <Select
                showSearch
                options={productPlansOptions}
                placeholder="Select product SKU"
                onChange={handleProductPlanChange}
                filterOption={(input, option) =>
                  (option?.label?.toString() || '').toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Product Type"
              name="productType"
              rules={[{ required: true, message: "Product type is required" }]}
            >
              <Select
                showSearch
                options={productTypesOptions}
                placeholder="Select product type"
                onChange={handleProductTypeChange}
                filterOption={(input, option) =>
                  (option?.label?.toString() || '').toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Expiry Date"
              name="expiryDate"
              rules={[{ required: true, message: "Choose valid expiry date" , validator: (_, value) => {
                if (value && value < dayjs().add(-1, 'day')) {
                  return Promise.reject(new Error("Choose valid expiry date"));
                }
                return Promise.resolve();
              } }]}
            >
              <DatePicker placeholder="Select expiry date" style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Activation Date"
              name="activationDate"
            >
              <DatePicker placeholder="Select activation date" style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Activated Date"
              name="activatedDate"
            >
              <DatePicker placeholder="Select activated date" style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Order Number"
              name="ordernumber"
            >
              <Input placeholder="Enter order number" />
            </Form.Item>
          </Col>
          {/* <Col span={12}>
            <Form.Item
              label="Inward Time"
              name="inwardTime"
            >
              <DatePicker 
                placeholder="Select inward time" 
                style={{ width: "100%" }} 
                showTime={{ format: 'HH:mm' }}
                format="YYYY-MM-DD HH:mm"
              />
            </Form.Item>
          </Col> */}
        </Row>

        {/* <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              label="Inward Time"
              name="inwardTime"
            >
              <DatePicker 
                placeholder="Select inward time" 
                style={{ width: "100%" }} 
                showTime={{ format: 'HH:mm' }}
                format="YYYY-MM-DD HH:mm"
              />
            </Form.Item>
          </Col>
        </Row> */}

       
      </Form>
    </Modal>
  );
}
